import Loader from "components/shared/loader";
import Table from "components/shared/table";
import { paymentMethodTypes, paymentPeriodTypes } from "helpers/enums";
import { GEOZONE_FIELDS_BY_REGION_TYPES } from "helpers/map";
import { DebouncedDefaultColumnFilter, DebouncedRangeColumnFilter } from "helpers/reactTable";
import { alphabeticalSort, enumLabel, formatNumber } from "helpers/system";
import upperFirst from "lodash/upperFirst";
import { matchSorter } from "match-sorter";
import React, { useEffect, useMemo, useState } from "react";
import { getCounterpartiesInRegion } from "sources/books/planning";

const tableDefaultProps = {
    minRows: 10,
    noDataText: "Нет данных для отображения",
    loadingComponent: <Loader />,
    pageSizeOptions: [30, 50, 100],
};

export default function CounterpartiesInRegion(props) {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [original, setOriginal] = useState([]);
    const [filtered, setFiltered] = useState({});

    useEffect(() => {
        const params = { [GEOZONE_FIELDS_BY_REGION_TYPES[props.regionType]]: props.regionId };
        getCounterpartiesInRegion(params).then((res) => {
            setData(res);
            setOriginal(res);
            setLoading(false);
        });
    }, []);

    const tableColumns = useMemo(
        () => [
            {
                Header: "#",
                id: "row_number",
                width: 50,
                sortable: false,
                notExportable: true,
                disableSortBy: true,
            },
            {
                Header: "Название",
                accessor: "displayName",
                sortType: (rowA, rowB, columnId) => alphabeticalSort(rowA.original, rowB.original, columnId),
                filterable: true,
            },
            {
                Header: "Кол-во заявок",
                accessor: "bidsAmount",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value }) => formatNumber(value),
            },
            {
                Header: `${upperFirst(props.organization?.currencyData?.name)}/км`,
                accessor: "output",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value }) => formatNumber(value, { style: "currency", currency: "RUB" }),
            },
            {
                Header: `${upperFirst(props.organization?.currencyData?.name)}/км без НДС`,
                accessor: "outputWithoutVat",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value }) => formatNumber(value, { style: "currency", currency: "RUB" }),
            },
            {
                Header: `${upperFirst(props.organization?.currencyData?.name)}/час`,
                accessor: "rubPerHour",
                filterable: true,
                filterType: "numberRange",
                Filter: DebouncedRangeColumnFilter,
                Cell: ({ value }) => formatNumber(value, { style: "currency", currency: "RUB" }),
            },
            {
                Header: "Условия работы",
                accessor: "platforms",
                filterable: false,
                Cell: ({ value }) => {
                    return value?.map((platform) => (
                        <div className="pb-2" key={platform.url}>
                            <a href={platform.url} target="_blank" rel="noreferrer noopener">
                                {platform.name}
                            </a>
                            {platform.representativeFullName && (
                                <div>
                                    <b>Представитель</b>
                                    <div>{platform.representativeFullName}</div>
                                    <div>
                                        <i className="text-muted font-size-12">{platform.representativeEmail}</i>
                                    </div>
                                    <div>
                                        <i className="text-muted font-size-12">{platform.representativePhoneNumber}</i>
                                    </div>
                                </div>
                            )}
                            {!!platform.paymentTypeId && (
                                <div>
                                    <b>Оплата</b>
                                    {!!platform.paymentTypeId && (
                                        <div>
                                            <span className="text-muted">Тип:</span>{" "}
                                            {enumLabel(props.enums.paymentType, platform.paymentTypeId)}
                                        </div>
                                    )}
                                    {!!platform.paymentPeriodType && (
                                        <div>
                                            <span className="text-muted">Период:</span>{" "}
                                            {enumLabel(paymentPeriodTypes, platform.paymentPeriodType)}
                                        </div>
                                    )}
                                    {!!platform.paymentMethodType && (
                                        <div>
                                            <span className="text-muted">Метод:</span>{" "}
                                            {enumLabel(paymentMethodTypes, platform.paymentMethodType)}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ));
                },
            },
        ],
        [],
    );

    if (loading) {
        return <Loader />;
    }

    const filterItems = (original, filtered) =>
        Object.values(filtered).reduce((memo, { id, value, filterType }) => {
            if (!value) {
                return memo;
            }
            if (!["numberRange"].includes(filterType) && value) {
                value = value.trim();
            }
            switch (filterType) {
                case "numberRange": {
                    return memo.filter((item) => {
                        let val = true;
                        if (value.start) {
                            val = item[id] >= value.start;
                        }
                        if (value.end) {
                            val = val && item[id] <= value.end;
                        }
                        return val;
                    });
                }
                default:
                    return memo.filter(
                        (item) =>
                            matchSorter([item[id]], value, { threshold: matchSorter.rankings.CONTAINS }).length !== 0,
                    );
            }
        }, original);

    const reportProps = {
        data,
        columns: tableColumns,
        loading,
        defaultColumn: {
            sortable: true,
            Filter: DebouncedDefaultColumnFilter,
        },
        filtered,
        onFilteredChange: ({ id, ...rest }) => {
            const newFilter = filtered;
            newFilter[id] = { id, ...rest };
            setFiltered(newFilter);
            setData(filterItems(original, newFilter));
        },
    };
    return <Table {...tableDefaultProps} {...reportProps} />;
}
